<template>
  <div class="container">
    <!-- 통계 탭 네비게이션 -->
    <StatsTabs />

    <div class="header">
      <h1 class="title">📊 방문자 통계</h1>
      <p class="subtitle">
        사이트 방문자 데이터를 월별, 일별로 확인할 수 있습니다.
      </p>
      <div class="admin-exclude-control">
        <label class="checkbox-label">
          <input type="checkbox" v-model="excludeAdmin" class="admin-checkbox" />
          <span class="checkbox-text">관리자 접속 제외</span>
        </label>
      </div>
    </div>

    <!-- 로딩 상태 -->
    <div v-if="loading">
      <LoadingScreen loading-text="방문자 통계를 불러오는 중..." />
    </div>

    <!-- 통계 데이터 -->
    <div v-else class="stats-content">
      <!-- 요약 정보 -->
      <div class="summary">
        <div class="summaryCard">
          <h3>
            {{
              selectedMonth ? selectedMonthLabel + " 방문자" : "이번 달 방문자"
            }}
          </h3>
          <p class="summaryNumber">
            {{ selectedMonth ? selectedMonthVisitors : currentMonthVisitors }}
          </p>
        </div>
        <div class="summaryCard">
          <h3>
            {{ selectedMonth ? selectedMonthLabel + " 일평균" : "전체 방문자" }}
          </h3>
          <p class="summaryNumber">
            {{ selectedMonth ? selectedMonthDailyAverage : totalVisitors }}
          </p>
        </div>
        <div class="summaryCard">
          <h3>
            {{
              selectedMonth ? selectedMonthLabel + " 일수" : "평균 일일 방문자"
            }}
          </h3>
          <p class="summaryNumber">
            {{ selectedMonth ? selectedMonthDays : averageDailyVisitors }}
          </p>
        </div>
      </div>

      <div class="chart-section">
        <div class="chart-card">
          <div class="card-title">
            <i class="pi pi-chart-line"></i>
            월간 방문자 수
            <span v-if="selectedMonth" class="selected-month-info">
              ({{ selectedMonthLabel }} 선택됨)
            </span>
          </div>
          <div class="chart-content">
            <div class="chart-info">
              <p class="chart-description">
                차트의 막대를 클릭하여 해당 월의 일별 통계를 확인하세요
              </p>
            </div>
            <Chart ref="monthlyChart" type="bar" :data="chartData" :options="chartOptions" class="visitor-chart" />
          </div>
        </div>

        <!-- 일별 방문자 차트 (월 선택 시 표시) -->
        <div class="daily-chart-card">
          <div class="card-title">
            <i class="pi pi-calendar"></i>
            {{
              selectedMonth
                ? selectedMonthLabel + " 일별 방문자 수"
                : "일별 방문자 수"
            }}
          </div>
          <div class="chart-content">
            <div class="chart-info">
              <p class="chart-description">
                자세한 내용은 차트 위에 커서를 올려서 확인해주세요
              </p>
            </div>
            <Chart v-if="selectedMonth && dailyChartData" type="line" :data="dailyChartData"
              :options="dailyChartOptions" class="daily-chart" />
          </div>
        </div>
      </div>

      <!-- 메뉴 클릭 통계 섹션 -->
      <div class="events-section">
        <div class="section-header">
          <h2 class="section-title">🔍 메뉴별 클릭 통계</h2>
          <p class="section-subtitle">
            {{ selectedMonth ? selectedMonthLabel : "전체" }} 기간의 메뉴 클릭
            통계입니다
          </p>
        </div>

        <div class="events-grid">
          <div class="event-card">
            <div class="event-icon">🎤</div>
            <div class="event-info">
              <h3 class="event-title">아티스트 메뉴</h3>
              <p class="event-count">{{ statisticsData.menu_artist }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">💬</div>
            <div class="event-info">
              <h3 class="event-title">커뮤니티 메뉴</h3>
              <p class="event-count">{{ statisticsData.menu_community }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">🏛️</div>
            <div class="event-info">
              <h3 class="event-title">공연장 메뉴</h3>
              <p class="event-count">{{ statisticsData.menu_venue }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">🔔</div>
            <div class="event-info">
              <h3 class="event-title">알림 메뉴</h3>
              <p class="event-count">{{ statisticsData.menu_notifications }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">📅</div>
            <div class="event-info">
              <h3 class="event-title">달력 메뉴</h3>
              <p class="event-count">{{ statisticsData.menu_calendar }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">👤</div>
            <div class="event-info">
              <h3 class="event-title">프로필 메뉴</h3>
              <p class="event-count">{{ statisticsData.menu_profile }}</p>
              <span class="event-label">회</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 이벤트 통계 섹션 -->
      <div class="events-section">
        <div class="section-header">
          <h2 class="section-title">📈 이벤트 통계</h2>
          <p class="section-subtitle">
            {{ selectedMonth ? selectedMonthLabel : "전체" }} 기간의 사용자
            이벤트 통계입니다
          </p>
        </div>

        <div class="events-grid">
          <div class="event-card">
            <div class="event-icon">🎭</div>
            <div class="event-info">
              <h3 class="event-title">공연 상세 조회</h3>
              <p class="event-count">{{ statisticsData.liveDetailView }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">🎫</div>
            <div class="event-info">
              <h3 class="event-title">예매하기 클릭</h3>
              <p class="event-count">{{ statisticsData.ticketClick }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">🔗</div>
            <div class="event-info">
              <h3 class="event-title">공연 링크 이동</h3>
              <p class="event-count">{{ statisticsData.liveLinkClick }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">🎤</div>
            <div class="event-info">
              <h3 class="event-title">아티스트 상세 조회</h3>
              <p class="event-count">{{ statisticsData.artistDetailView }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">🌐</div>
            <div class="event-info">
              <h3 class="event-title">아티스트 링크 이동</h3>
              <p class="event-count">{{ statisticsData.artistLinkClick }}</p>
              <span class="event-label">회</span>
            </div>
          </div>

          <div class="event-card">
            <div class="event-icon">💬</div>
            <div class="event-info">
              <h3 class="event-title">커뮤니티 조회</h3>
              <p class="event-count">{{ statisticsData.communityView }}</p>
              <span class="event-label">회</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/store/user";
import { rtdb } from "/firebaseConfig";
import { ref as dbRef, get } from "firebase/database";
import Chart from "primevue/chart";
import StatsTabs from "@/components/StatsTabs.vue"; // Added import for StatsTabs
import LoadingScreen from "@/components/LoadingScreen.vue"; // Added import for LoadingScreen

export default {
  name: "VisitorStats",
  components: {
    Chart,
    StatsTabs, // Added StatsTabs to components
    LoadingScreen, // Added LoadingScreen to components
  },
  setup() {
    const router = useRouter();
    const userStore = useUserStore();

    // 관리자 권한 확인
    if (userStore.auth !== "admin") {
      router.push("/");
      return;
    }

    const visitorData = ref({});
    const adminData = ref({});
    const loading = ref(true);
    const selectedMonth = ref(null);
    const monthlyChart = ref(null);
    const excludeAdmin = ref(false);
    const rawStatisticsData = ref({});
    const rawAdminStatisticsData = ref({});

    // 실제 방문자 수 계산 함수 (관리자 제외 옵션 고려)
    const getActualVisitorCount = (date) => {
      const visitorCount = visitorData.value[date];
      let count = 0;

      if (typeof visitorCount === "number") {
        count = visitorCount;
      } else if (
        typeof visitorCount === "object" &&
        visitorCount?.all !== undefined
      ) {
        count = visitorCount.all;
      }

      if (excludeAdmin.value) {
        const adminCount = adminData.value[date];
        let adminVisits = 0;

        if (typeof adminCount === "number") {
          adminVisits = adminCount;
        } else if (
          typeof adminCount === "object" &&
          adminCount?.all !== undefined
        ) {
          adminVisits = adminCount.all;
        }

        count = Math.max(0, count - adminVisits);
      }

      return count;
    };

    // 차트 데이터
    const chartData = computed(() => {
      const dates = Object.keys(visitorData.value).sort();

      // 날짜를 월별로 그룹화
      const monthGroups = {};
      dates.forEach((date) => {
        const [year, month] = date.split("-").slice(0, 2);
        const monthKey = `${year}-${month}`;
        if (!monthGroups[monthKey]) {
          monthGroups[monthKey] = [];
        }
        monthGroups[monthKey].push(date);
      });

      const sortedMonths = Object.keys(monthGroups).sort();

      const datasets = [
        {
          label: "방문자 수",
          data: sortedMonths.map((monthKey) => {
            // 해당 월의 모든 날짜 데이터 합계
            return monthGroups[monthKey].reduce((sum, date) => {
              return sum + getActualVisitorCount(date);
            }, 0);
          }),
          backgroundColor: "rgba(33, 150, 243, 0.8)",
          borderColor: "#2196f3",
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
        },
      ];

      return {
        labels: sortedMonths.map((monthKey) => {
          const month = monthKey.split("-")[1];
          return `${month}월`;
        }),
        datasets,
      };
    });

    // 월별 옵션 생성
    const monthOptions = computed(() => {
      const dates = Object.keys(visitorData.value).sort();

      // 날짜를 월별로 그룹화
      const monthGroups = {};
      dates.forEach((date) => {
        const [year, month] = date.split("-").slice(0, 2); // YYYY-MM 부분만 추출
        const monthKey = `${year}-${month}`;
        if (!monthGroups[monthKey]) {
          monthGroups[monthKey] = [];
        }
        monthGroups[monthKey].push(date);
      });

      return Object.keys(monthGroups)
        .sort()
        .reverse() // 내림차순으로 변경
        .map((monthKey) => {
          const [year, month] = monthKey.split("-");
          return {
            label: `${year}년 ${month}월`,
            value: monthKey,
          };
        });
    });

    // 선택된 월의 라벨
    const selectedMonthLabel = computed(() => {
      if (!selectedMonth.value) return "";
      const [year, monthNum] = selectedMonth.value.split("-");
      return `${year}년 ${monthNum}월`;
    });

    // 일별 차트 데이터
    const dailyChartData = computed(() => {
      if (!selectedMonth.value) {
        // console.log("selectedMonth가 없음");
        return null;
      }

      // 선택된 월에 해당하는 날짜들 찾기
      const dates = Object.keys(visitorData.value)
        .filter((date) => date.startsWith(selectedMonth.value))
        .sort();

      if (dates.length === 0) {
        // console.log("해당 월의 날짜가 없음");
        return null;
      }

      const chartData = {
        labels: dates.map((date) => {
          const day = date.split("-")[2]; // 일자 부분만 추출
          return `${day}일`;
        }),
        datasets: [
          {
            label: "일별 방문자",
            data: dates.map((date) => {
              return getActualVisitorCount(date);
            }),
            borderColor: "#ff6b35",
            backgroundColor: "rgba(255, 107, 53, 0.1)",
            borderWidth: 3,
            fill: true,
            tension: 0.4,
          },
        ],
      };

      return chartData;
    });

    // 일별 차트 옵션
    const dailyChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "white",
          bodyColor: "white",
          borderColor: "#ff6b35",
          borderWidth: 1,
          callbacks: {
            label: function (context) {
              return `방문자: ${context.parsed.y}명`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            callback: function (value) {
              return value + "명";
            },
            color: "#666",
          },
        },
        x: {
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            color: "#666",
          },
        },
      },
    };

    // 차트 옵션
    const chartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event, elements) => {
        if (elements && elements.length > 0) {
          const index = elements[0].index;

          // 원본 monthKey 배열에서 해당 인덱스의 월 키 가져오기
          const dates = Object.keys(visitorData.value).sort();
          const monthGroups = {};
          dates.forEach((date) => {
            const [year, month] = date.split("-").slice(0, 2);
            const monthKey = `${year}-${month}`;
            if (!monthGroups[monthKey]) {
              monthGroups[monthKey] = [];
            }
            monthGroups[monthKey].push(date);
          });

          const sortedMonths = Object.keys(monthGroups).sort();
          if (sortedMonths[index]) {
            const monthKey = sortedMonths[index];
            selectedMonth.value = monthKey;
          }
        }
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "white",
          bodyColor: "white",
          borderColor: "#2196f3",
          borderWidth: 1,
          callbacks: {
            title: function (context) {
              // x축 라벨에 년도 정보 추가
              const index = context[0].dataIndex;
              const dates = Object.keys(visitorData.value).sort();
              const monthGroups = {};
              dates.forEach((date) => {
                const [year, month] = date.split("-").slice(0, 2);
                const monthKey = `${year}-${month}`;
                if (!monthGroups[monthKey]) {
                  monthGroups[monthKey] = [];
                }
                monthGroups[monthKey].push(date);
              });

              const sortedMonths = Object.keys(monthGroups).sort();
              if (sortedMonths[index]) {
                const [year, month] = sortedMonths[index].split("-");
                return `${year}년 ${month}월`;
              }
              return context[0].label;
            },
            label: function (context) {
              return `방문자: ${context.parsed.y}명`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            callback: function (value) {
              return value + "명";
            },
            color: "#666",
          },
        },
        x: {
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            color: "#666",
          },
        },
      },
    }));

    // 이번 달 방문자 수
    const currentMonthVisitors = computed(() => {
      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
      const dates = Object.keys(visitorData.value).filter((date) =>
        date.startsWith(currentMonth)
      );

      return dates.reduce((sum, date) => {
        return sum + getActualVisitorCount(date);
      }, 0);
    });

    // 전체 방문자 수
    const totalVisitors = computed(() => {
      return Object.keys(visitorData.value).reduce((total, date) => {
        return total + getActualVisitorCount(date);
      }, 0);
    });

    // 평균 일일 방문자 수
    const averageDailyVisitors = computed(() => {
      const totalDays = Object.keys(visitorData.value).length;
      const totalVisitorsCount = Object.keys(visitorData.value).reduce(
        (sum, date) => {
          return sum + getActualVisitorCount(date);
        },
        0
      );

      return totalDays > 0 ? Math.round(totalVisitorsCount / totalDays) : 0;
    });

    // 선택된 월의 방문자 수
    const selectedMonthVisitors = computed(() => {
      if (!selectedMonth.value) return 0;
      const monthData = Object.keys(visitorData.value)
        .filter((date) => date.startsWith(selectedMonth.value))
        .reduce((sum, date) => {
          return sum + getActualVisitorCount(date);
        }, 0);
      return monthData;
    });

    // 선택된 월의 평균 일일 방문자 수
    const selectedMonthDailyAverage = computed(() => {
      if (!selectedMonth.value) return 0;
      const monthDates = Object.keys(visitorData.value).filter((date) =>
        date.startsWith(selectedMonth.value)
      );
      if (monthDates.length === 0) return 0;

      const monthTotal = monthDates.reduce((sum, date) => {
        return sum + getActualVisitorCount(date);
      }, 0);

      return Math.round(monthTotal / monthDates.length);
    });

    // 선택된 월의 데이터 있는 일수
    const selectedMonthDays = computed(() => {
      if (!selectedMonth.value) return 0;
      return Object.keys(visitorData.value).filter((date) =>
        date.startsWith(selectedMonth.value)
      ).length;
    });

    // 이벤트 통계 데이터 계산
    const statisticsData = computed(() => {
      const eventTypes = [
        "liveDetailView",
        "ticketClick",
        "liveLinkClick",
        "artistDetailView",
        "artistLinkClick",
        "communityView",
        "menu_artist",
        "menu_community",
        "menu_venue",
        "menu_notifications",
        "menu_calendar",
        "menu_profile",
      ];

      const result = {};

      eventTypes.forEach((eventType) => {
        let count = 0;
        const eventData = rawStatisticsData.value[eventType] || {};
        const adminEventData = rawAdminStatisticsData.value[eventType] || {};

        // 날짜별로 순회
        Object.keys(eventData).forEach((date) => {
          // 선택된 월이 있으면 해당 월의 데이터만, 없으면 전체
          if (!selectedMonth.value || date.startsWith(selectedMonth.value)) {
            const dayData = eventData[date];
            // total 값을 가져옴
            if (dayData && typeof dayData.total === "number") {
              count += dayData.total;
            }
          }
        });

        // 관리자 제외 옵션이 활성화된 경우
        if (excludeAdmin.value) {
          Object.keys(adminEventData).forEach((date) => {
            if (!selectedMonth.value || date.startsWith(selectedMonth.value)) {
              const adminDayData = adminEventData[date];
              if (adminDayData && typeof adminDayData.total === "number") {
                count -= adminDayData.total;
              }
            }
          });
          // 음수가 되지 않도록 보정
          count = Math.max(0, count);
        }

        result[eventType] = count;
      });

      return result;
    });

    // 월 변경 핸들러
    const onMonthChange = () => {
      // 월이 변경되면 차트가 자동으로 업데이트됩니다
    };

    // 관리자 데이터 가져오기
    const fetchAdminData = async () => {
      try {
        const adminRef = dbRef(rtdb, "adminCount");
        const snapshot = await get(adminRef);

        if (snapshot.exists()) {
          adminData.value = snapshot.val();
        } else {
          // console.log("No admin data found");
          adminData.value = {};
        }
      } catch (error) {
        console.error("관리자 데이터 가져오기 실패:", error);
        adminData.value = {};
      }
    };

    // 이벤트 통계 데이터 가져오기
    const fetchStatisticsData = async () => {
      try {
        const statisticsRef = dbRef(rtdb, "statistics");
        const snapshot = await get(statisticsRef);

        if (snapshot.exists()) {
          rawStatisticsData.value = snapshot.val();
        } else {
          rawStatisticsData.value = {};
        }
      } catch (error) {
        console.error("통계 데이터 가져오기 실패:", error);
        rawStatisticsData.value = {};
      }
    };

    // 관리자 이벤트 통계 데이터 가져오기
    const fetchAdminStatisticsData = async () => {
      try {
        const adminStatisticsRef = dbRef(rtdb, "statisticsAdmin");
        const snapshot = await get(adminStatisticsRef);

        if (snapshot.exists()) {
          rawAdminStatisticsData.value = snapshot.val();
        } else {
          rawAdminStatisticsData.value = {};
        }
      } catch (error) {
        console.error("관리자 통계 데이터 가져오기 실패:", error);
        rawAdminStatisticsData.value = {};
      }
    };

    // 방문자 데이터 가져오기
    const fetchVisitorData = async () => {
      try {
        loading.value = true;
        const visitorRef = dbRef(rtdb, "visitorCount");
        const snapshot = await get(visitorRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          visitorData.value = data;

          // 첫 번째 월을 자동으로 선택
          if (Object.keys(data).length > 0) {
            const sortedMonths = Object.keys(data).sort().reverse();
            const firstMonth = sortedMonths[0].split("-").slice(0, 2).join("-");
            selectedMonth.value = firstMonth;
          }
        } else {
          // console.log("No visitor data found");
        }

        // 관리자 데이터와 통계 데이터도 함께 가져오기
        await fetchAdminData();
        await fetchStatisticsData();
        await fetchAdminStatisticsData();
      } catch (error) {
        console.error("방문자 데이터 가져오기 실패:", error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchVisitorData();
    });

    return {
      visitorData,
      adminData,
      loading,
      selectedMonth,
      monthOptions,
      selectedMonthLabel,
      dailyChartData,
      dailyChartOptions,
      chartData,
      chartOptions,
      currentMonthVisitors,
      totalVisitors,
      averageDailyVisitors,
      onMonthChange,
      selectedMonthVisitors,
      selectedMonthDailyAverage,
      selectedMonthDays,
      monthlyChart,
      excludeAdmin,
      getActualVisitorCount,
      statisticsData,
    };
  },
};
</script>

<style scoped>
.container {
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  padding: 0 15px;
  min-height: 100vh;
}

.header {
  text-align: center;
  padding: 25px;
  border-radius: 15px;
}

.header h1 {
  font-size: 32px;
  color: #333;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header h1 i {
  color: #2196f3;
  font-size: 36px;
}

.header .title {
  font-size: 2rem;
  color: #333;
  margin: 0 0 10px 0;
  font-weight: bold;
  display: block;
  font-family: "NanumSquareRoundR", sans-serif;
}

.header .subtitle {
  font-size: 1rem;
  color: #666;
  margin: 0;
}

.admin-exclude-control {
  margin-top: 15px;
  display: flex;
  justify-content: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 15px;
  color: #333;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 9px 18px;
  background: white;
  border-radius: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.checkbox-label:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.admin-checkbox {
  width: 17px;
  height: 17px;
  border: 2px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.admin-checkbox:checked {
  background-color: #2196f3;
  border-color: #2196f3;
}

.checkbox-text {
  font-weight: 500;
  color: #333;
}

.stats-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 0px;
}

.summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  color: #333;
  margin-bottom: 15px;
}

.summaryCard {
  background: white;
  text-align: center;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.summaryCard:hover {
  transform: translateY(-2px);
}

.summaryCard h3 {
  margin: 0 0 8px 0;
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  opacity: 0.9;
  line-height: 1.2;
}

.summaryNumber {
  margin: 0;
  font-size: 1.6rem;
  font-weight: bold;
  color: #333;
  line-height: 1.1;
}

.chart-section {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.chart-card {
  background: white;
  padding: 18px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  border: none;
  margin-bottom: 0;
  min-width: 100%;
}

.daily-chart-card {
  background: white;
  padding: 18px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  border: none;
  margin-top: 0;
  min-width: 100%;
}

.chart-content {
  margin-top: 10px;
}

.chart-controls {
  margin-bottom: 12px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 12px;
}

.selected-month-info {
  color: #2196f3;
  font-size: 13px;
  font-weight: normal;
  margin-left: 8px;
}

.month-selector {
  width: 100%;
  max-width: 300px;
}

.month-selector :deep(.p-dropdown) {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: white;
}

.month-selector :deep(.p-dropdown:hover) {
  border-color: #2196f3;
}

.month-selector :deep(.p-dropdown:focus-within) {
  border-color: #2196f3;
  box-shadow: 0 0 0 0.2rem rgba(33, 150, 243, 0.25);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  color: #333;
  font-weight: 600;
  padding: 4px 0;
  margin-bottom: 8px;
}

.card-title i {
  color: #2196f3;
  font-size: 16px;
}

.visitor-chart {
  height: 260px;
  margin-top: 10px;
  padding: 8px 0;
}

.daily-chart {
  height: 260px;
  margin-top: 10px;
  padding: 8px 0;
}

.chart-info {
  margin-bottom: 8px;
  text-align: center;
}

.chart-description {
  color: #666;
  font-size: 0.85rem;
  margin: 0;
  font-style: italic;
}

/* 이벤트 통계 섹션 */
.events-section {
  margin-top: 30px;
  padding: 25px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.section-header {
  margin-bottom: 25px;
  text-align: center;
}

.section-title {
  font-size: 1.6rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.section-subtitle {
  color: #666;
  font-size: 0.95rem;
}

.events-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 15px;
}

.event-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.event-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #d0d0d0;
}

.event-icon {
  font-size: 2.5rem;
  margin-right: 15px;
  opacity: 0.8;
}

.event-info {
  flex: 1;
}

.event-title {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
}

.event-count {
  font-size: 2.2rem;
  font-weight: bold;
  color: #333;
  margin: 0;
  line-height: 1;
}

.event-label {
  font-size: 0.85rem;
  color: #888;
  margin-left: 5px;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0 10px 10px 10px;
  }

  .header {
    padding: 15px 10px;
  }

  .header .title {
    font-size: 1.5rem;
    margin-bottom: 8px;
  }

  .header .subtitle {
    font-size: 0.85rem;
  }

  .admin-exclude-control {
    margin-top: 12px;
  }

  .checkbox-label {
    font-size: 13px;
    padding: 8px 14px;
    border-radius: 20px;
  }

  .admin-checkbox {
    width: 15px;
    height: 15px;
  }

  .checkbox-text {
    font-size: 13px;
  }

  .stats-content {
    gap: 15px;
  }

  .summary {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }

  .summaryCard {
    padding: 10px 6px;
    border-radius: 8px;
  }

  .summaryCard h3 {
    font-size: 0.6rem;
    margin-bottom: 6px;
  }

  .summaryNumber {
    font-size: 1rem;
  }

  .chart-section {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .chart-card,
  .daily-chart-card {
    padding: 12px;
    border-radius: 8px;
  }

  .card-title {
    font-size: 0.9rem;
    gap: 6px;
    margin-bottom: 6px;
  }

  .card-title i {
    font-size: 14px;
  }

  .selected-month-info {
    font-size: 11px;
    margin-left: 4px;
  }

  .chart-info {
    margin-bottom: 6px;
  }

  .chart-description {
    font-size: 0.75rem;
  }

  .visitor-chart,
  .daily-chart {
    height: 200px;
    margin-top: 8px;
    padding: 4px 0;
  }

  .events-section {
    margin-top: 20px;
    padding: 15px;
    border-radius: 10px;
  }

  .section-header {
    margin-bottom: 15px;
  }

  .section-title {
    font-size: 1.3rem;
    margin-bottom: 6px;
  }

  .section-subtitle {
    font-size: 0.8rem;
  }

  .events-grid {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .event-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 12px 8px;
    border-radius: 8px;
  }

  .event-icon {
    font-size: 1.8rem;
    margin-right: 0;
    margin-bottom: 8px;
  }

  .event-info {
    width: 100%;
  }

  .event-title {
    font-size: 0.75rem;
    margin-bottom: 4px;
  }

  .event-count {
    font-size: 1.5rem;
  }

  .event-label {
    font-size: 0.7rem;
    margin-left: 3px;
  }
}
</style>
